import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"
import { useDialogBoundary } from "@/contexts/DialogBoundaryContext"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <PopoverPrimitive.Trigger
    ref={ref}
    className={cn(
      "touch-target outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  >
    {children}
  </PopoverPrimitive.Trigger>
))
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName

interface PopoverContentProps extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  collisionBoundary?: Element | Element[] | null;
  collisionPadding?: number | { top?: number; right?: number; bottom?: number; left?: number };
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = "center", sideOffset = 4, collisionBoundary: externalBoundary, collisionPadding = 16, ...props }, ref) => {
  const dialogBoundary = useDialogBoundary();
  const effectiveBoundary = externalBoundary ?? dialogBoundary ?? undefined;
  
  return (
    <PopoverPrimitive.Portal container={document.body}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionBoundary={effectiveBoundary}
        collisionPadding={collisionPadding}
        avoidCollisions={true}
        className={cn(
          "popover-enhanced rounded-xl border bg-popover p-4 text-popover-foreground shadow-lg outline-none",
          "w-[calc(100vw-2rem)] sm:w-72 max-w-[calc(100vw-2rem)]",
          effectiveBoundary ? "max-h-[50vh]" : "max-h-[70vh] sm:max-h-[80vh]",
          "overflow-y-auto",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }
